/**
 * GENESIS API client
 *
 * Supports both GENESIS instances:
 * - destatis: GENESIS-Online (Bund/Länder)
 * - regionalstatistik: Regionalstatistik (Kreis/Gemeinde)
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin/genesis`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Types
// ============================================================================

export type GenesisInstance = 'destatis' | 'regionalstatistik'

export interface GenesisInstanceInfo {
  id: GenesisInstance
  name: string
  data_levels: string[]
  configured: boolean
  connected: boolean
  username: string | null
}

export interface RSApiStatus {
  connected: boolean
  configured: boolean
  username: string | null
  message: string
}

export interface RSTableSearchResult {
  code: string
  name: string
  statistics_code: string
  statistics_name: string
  regional_depth: string
  is_gemeinde_level: boolean
  is_kreis_level: boolean
  source: GenesisInstance
  levels: string[]  // 'bund', 'land', 'kreis', 'gemeinde'
}

export interface RSInstanceSearchStatus {
  instance: GenesisInstance
  configured: boolean
  success: boolean
  result_count: number
  error: string | null
}

export interface RSUnifiedSearchResponse {
  results: RSTableSearchResult[]
  instance_status: RSInstanceSearchStatus[]
}

export interface RSTableMetadata {
  code: string
  name: string
  time_from: string | null
  time_to: string | null
  latest_year: number | null
  available_years: number[]
}

export interface RSIndicatorPreview {
  code: string
  name: string
  unit: string
  sample_values: (number | null)[]
  entity_count: number
}

export interface RSUploadResponse {
  upload_id: string
  filename: string
  table_code: string
  table_name: string
  time_label: string
  time_value: string
  detected_year: number
  entity_counts: Record<string, number>
  indicators: RSIndicatorPreview[]
  row_count: number
  column_count: number
}

export interface RSExistingDataCheck {
  exists: boolean
  data_source_id: number | null
  table_code: string
  reference_year: number
  records_count: number
  import_date: string | null
  message: string
}

export interface RSImportExecuteRequest {
  upload_id?: string
  table_code?: string
  year?: number
  target_edition_id: number
  entity_levels?: string[]
  indicator_codes?: string[]
  force_update?: boolean
}

export interface RSImportResponse {
  job_id: string
  status: 'started' | 'completed' | 'failed'
  message: string
  background: boolean
  result?: RSImportResult
}

export interface RSImportResult {
  status: 'completed' | 'partial' | 'failed'
  total_records: number
  successful_records: number
  failed_records: number
  skipped_records: number
  duration_seconds: number
  success_rate: number
  errors: string[]
  warnings: string[]
  metadata: Record<string, unknown>
  data_source_id: number | null
  table_code: string | null
  reference_year: number | null
}

export interface RSImportStatusResponse {
  job_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number | null
  message: string | null
  result: RSImportResult | null
}

export interface RSIndicatorInfo {
  code: string
  name: string
  unit: string
}

export interface RSDataSourceInfo {
  id: number
  // Basic info
  table_code: string
  statistics_code: string
  statistics_label: string
  // Time reference
  reference_year: number
  time_label: string
  time_value: string
  // Import info
  import_date: string
  records_imported: number
  is_latest: boolean
  // Content metadata
  indicators: RSIndicatorInfo[]
  entity_levels: string[]
  entity_counts: Record<string, number>
  // Parent edition
  parent_edition_id: number | null
  parent_edition_name: string | null
}

// ============================================================================
// API Status & Instances
// ============================================================================

export async function getInstances(): Promise<GenesisInstanceInfo[]> {
  const response = await api.get<GenesisInstanceInfo[]>('/api/instances')
  return response.data
}

export async function getApiStatus(instance: GenesisInstance = 'regionalstatistik'): Promise<RSApiStatus> {
  const response = await api.get<RSApiStatus>('/api/status', {
    params: { instance },
  })
  return response.data
}

// ============================================================================
// Table Search
// ============================================================================

export interface SearchParams {
  term: string
  instance?: GenesisInstance
  gemeindeOnly?: boolean
  kreisOnly?: boolean
  limit?: number
}

export interface UnifiedSearchParams {
  term: string
  levelFilter?: 'bund' | 'land' | 'kreis' | 'gemeinde'
}

export async function searchTables(params: SearchParams): Promise<RSTableSearchResult[]> {
  const queryParams = new URLSearchParams()
  queryParams.set('term', params.term)
  queryParams.set('instance', params.instance || 'regionalstatistik')
  if (params.gemeindeOnly) queryParams.set('gemeinde_only', 'true')
  if (params.kreisOnly) queryParams.set('kreis_only', 'true')
  if (params.limit) queryParams.set('limit', params.limit.toString())

  const response = await api.get<RSTableSearchResult[]>(`/api/search?${queryParams}`)
  return response.data
}

/**
 * Search tables across ALL configured GENESIS instances.
 * Returns combined results from both destatis and regionalstatistik.
 */
export async function searchTablesAll(params: UnifiedSearchParams): Promise<RSUnifiedSearchResponse> {
  const queryParams = new URLSearchParams()
  queryParams.set('term', params.term)
  if (params.levelFilter) queryParams.set('level_filter', params.levelFilter)

  const response = await api.get<RSUnifiedSearchResponse>(`/api/search-all?${queryParams}`)
  return response.data
}

export async function getTableMetadata(
  code: string,
  instance: GenesisInstance = 'regionalstatistik'
): Promise<RSTableMetadata> {
  const response = await api.get<RSTableMetadata>(`/api/tables/${code}`, {
    params: { instance },
  })
  return response.data
}

// ============================================================================
// Upload
// ============================================================================

export async function uploadFlatCSV(file: File): Promise<RSUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post<RSUploadResponse>('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export async function getUploadPreview(uploadId: string): Promise<{
  upload_id: string
  summary: Record<string, unknown>
  sample_records: Record<string, unknown>[]
}> {
  const response = await api.get(`/upload/${uploadId}/preview`)
  return response.data
}

// ============================================================================
// Import Execution
// ============================================================================

export async function checkExistingImport(
  tableCode: string,
  referenceYear: number
): Promise<RSExistingDataCheck> {
  const response = await api.get<RSExistingDataCheck>('/import/check-existing', {
    params: { table_code: tableCode, reference_year: referenceYear },
  })
  return response.data
}

export async function executeImport(request: RSImportExecuteRequest): Promise<RSImportResponse> {
  const response = await api.post<RSImportResponse>('/import/execute', request)
  return response.data
}

export async function getImportStatus(jobId: string): Promise<RSImportStatusResponse> {
  const response = await api.get<RSImportStatusResponse>(`/import/status/${jobId}`)
  return response.data
}

// ============================================================================
// Data Browser
// ============================================================================

export async function getImportedData(params?: {
  table_code?: string
  limit?: number
  offset?: number
}): Promise<RSDataSourceInfo[]> {
  const queryParams = new URLSearchParams()
  if (params?.table_code) queryParams.set('table_code', params.table_code)
  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.offset) queryParams.set('offset', params.offset.toString())

  const queryString = queryParams.toString()
  const url = queryString ? `/data?${queryString}` : '/data'

  const response = await api.get<RSDataSourceInfo[]>(url)
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
